/**
 * All updatable fields of the metadata, i.e. the _actual_ metadata
 */
import type { ISampleMetaData, NewSampleMetaData, PureMetadata } from '../../../../Model/Sample/ISampleMetaData.js'
import type { EItemType, FileForProcessing, NegativeControl, Sample } from '../../../../Model/index.js'

export type CSVMappingResult = {
  metadata: PureMetadata,
  /**
   * Fields the row asked to empty, via the clear sentinel. Separate from [metadata] because an absent
   * key there means "this row said nothing about the column", which must leave the stored value alone.
   */
  clearedFields?: readonly (keyof PureMetadata)[],
  forwardPrimer?: string,
  reversePrimer?: string,
  sequenceId?: string,
  customerSampleName?: string,
  sampleId?: string,
  /** The type the sheet states for the row; absent when it states none, which leaves the stored type alone. */
  itemType?: EItemType,
  /** The pool the sheet assigns the row to; `key` groups rows, `name` is the label it may give the pool. */
  pool?: { key: string, name?: string },
}

export type CSVMappingFlags = {
  overrideCustomerPrimer: boolean,
  overrideCustomerData: boolean,
}

export type CSVMode =
  "PREPARE_METADATA"
  /* customer driven: prepare metadata without fastq files, creates dangling metadata without
  sample mapping and only customer sample name given */
  | "CREATE_SAMPLES"
  /* lab driven: provide sequencing id to merge fastq files into samples, creates samples and
  * possibly metadata, mapSamplePools or whatever should ask for confirmation before creating new metadata! */
  | "UPDATE_METADATA"
/* lab or customer driven: updates existing metadata after merging, allows sample id as key
(can also use other keys?) */

export type OpCreateSample = {
  sample: {
    itemType: EItemType,
    forwardFileId: string,
    reverseFileId?: string,
    isTrimmed: boolean,
    isMerged: boolean,
    customerSampleName?: string,
    forwardPrimer?: string,
    reversePrimer?: string,
  }
  metadata: NewSampleMetaData
  op: "CreateSample"
  orderId: string
  poolId: string
  /** The sheet pool the copy belongs in, resolved to a pool id once the import's pools exist. */
  poolKey?: string
}

export type OpUpdateSample = {
  sample: FileForProcessing
  op: "UpdateSample"
  orderId: string
  poolId: string
}

export type OpUpdateMetadata = {
  metadata: ISampleMetaData
  op: "UpdateMetadata"
  orderId: string
  poolId?: string
}

export type OpCreateMetadata = {
  metadata: NewSampleMetaData
  op: "CreateMetadata"
  orderId: string
  poolId?: string
}

export type MergedOperation = OpUpdateSample | OpUpdateMetadata | OpCreateMetadata | OpCreateSample

export function isOpCreateSample(op: MergedOperation): op is OpCreateSample {
  return op.op === "CreateSample" && "sample" in op;
}

export function isOpUpdateSample(op: MergedOperation): op is OpUpdateSample {
  return op.op === "UpdateSample" && "sample" in op;
}

export function isOpUpdateMetadata(op: MergedOperation): op is OpUpdateMetadata {
  return op.op === "UpdateMetadata" && "metadata" in op;
}

export function isOpCreateMetadata(op: MergedOperation): op is OpCreateMetadata {
  return op.op === "CreateMetadata" && "metadata" in op;
}

export type SampleOperationResult = {
  result: MergedOperation[]
  warnings: OpWarning[]
  errors: OpError[]
  /** Rows paired with the existing item they matched; a replica row copies its item into a new one. */
  matched?: MatchedRow[]
}

export type MatchedRow = { row: CSVMappingResult, item: FileForProcessing, replica: boolean }

export type SampleOperationResponse = {
  samples: (Sample | NegativeControl)[]
  newMetadata: ISampleMetaData[]
  updatedMetadata: ISampleMetaData[]
}

export type OpError = ErrEmptySampleName
  | ErrEmptySequenceId
  | ErrSampleIdAndMetaIdMismatch
  | ErrSampleWithoutMetadata
  | ErrMultiSampleNotAllowed
  | ErrCannotDuplicatePopulatedSample
  | ErrSampleReplicationNotAllowedInState
  | ErrSampleWithoutPoolId
  | ErrMissingOrder

export type OpWarning = WarnUnmatchedSequenceId | WarnMissingMetadataButPresent | WarnDuplicateFilenames | WarnDuplicateSampleName

export type ErrSampleWithoutMetadata = { type: "ErrSampleWithoutMetadata", sample: FileForProcessing }

export type ErrEmptySampleName = { type: "ErrEmptySampleName" }

export type ErrEmptySequenceId = { type: "ErrEmptySequenceId" }

export type ErrMultiSampleNotAllowed = { type: "ErrMultiSampleNotAllowed" }

export type ErrCannotDuplicatePopulatedSample = { type: "ErrCannotDuplicatePopulatedSample" }

export type ErrSampleReplicationNotAllowedInState = { type: 'ErrSampleReplicationNotAllowedInState' }

export type ErrMissingOrder = { type: "ErrMissingOrder" }

export type ErrSampleWithoutPoolId = { type: 'ErrSampleWithoutPoolId' }

export type ErrSampleIdAndMetaIdMismatch = { type: "ErrSampleIdAndMetaIdMismatch", sequenceId?: string, sampleId?: string, boundSampleId?: string, sampleName: string }

export type WarnUnmatchedSequenceId = { type: "WarnUnmatchedSequenceId", sequenceId?: string, sampleId?: string }

export type WarnMissingMetadataButPresent = { type: "WarnMissingMetadataButPresent", sequenceId?: string, sampleId?: string }

export type WarnDuplicateFilenames = { type: "WarnDuplicateFilenames" }

export type WarnDuplicateSampleName = { type: "WarnDuplicateSampleName" }

export const stringifyError = (error: OpError): string => {
  switch (error.type) {
    case 'ErrCannotDuplicatePopulatedSample': return "A sample with populated metadata cannot be duplicated, check your csv references"
    case 'ErrEmptySampleName': return "A sample name was empty, check your csv"
    case 'ErrSampleWithoutMetadata': return "A sample without metadata was encountered, id is " + error.sample.id
    case 'ErrEmptySequenceId': return "An empty sequence id was encountered, check your csv"
    case 'ErrSampleIdAndMetaIdMismatch': return `The metadata "${error.sampleName}" is already linked to a different sample (linked sampleId: ${error.boundSampleId ?? 'unknown'}; matched via sampleId: ${error.sampleId ?? 'n/a'}, sequenceId: ${error.sequenceId ?? 'n/a'}). Unlink or delete that metadata entry, then retry the import.`
    case 'ErrMultiSampleNotAllowed': return `Two rows matched to the same sample, but your order sample configuration does not allow multi-samples`
    case 'ErrSampleReplicationNotAllowedInState': return 'Sample replication is not allowed in the current state'
    case 'ErrSampleWithoutPoolId': return 'Encountered a sample without pool id, this should not be possible'
    case 'ErrMissingOrder': return 'An operation required a present order, but none was found'
    default: return JSON.stringify(error)
  }
}

export const stringifyWarning = (warning: OpWarning) => {
  switch (warning.type) {
    case 'WarnMissingMetadataButPresent':
      return `Sample with id ${warning.sampleId} and sequence id ${warning.sequenceId} will not be matched to existing metadata, but there is unmatched metadata present`
    case 'WarnDuplicateFilenames':
      return `At least one filename is duplicated, this can lead to unintended matches when matching by sequence id. Consider matching via sample id`
    case 'WarnUnmatchedSequenceId':
      return `A sequence id provided in the csv as ${warning.sequenceId} or a sample id provided as ${warning.sampleId} could not be matched to a sample and will be ignored`
    case 'WarnDuplicateSampleName':
      return 'A customer sample name is occurring twice. Matching of samples cannot be done via sample name until this is rectified'
    default:
      return JSON.stringify(warning)
  }
}